import type { Gender, User } from './user';
import type { Designation } from './designation';
import type { Department } from './department';
import type { GetListResponse } from './generic';
import type { OptionalPageLimit } from './common';
import type { Branch } from './branches';
import type { LeaveRowReportingManager } from './leave-management';

/**
 * Employee Type Definition
 */
export interface Employee {
  id: string;
  userId: string;
  designationId: string;
  departmentId: string;
  companyId: string;
  employeeReferenceId: string | null;
  status: boolean;
  branchId: string;
  branch: Branch;
  dateOfJoining: Date | undefined;
  employmentType: string | null;
  contractEndDate: string | null;
  partTimeWeeklyMinutes?: number | null;
  personalEmail?: string | null;
  workEmail: string | null;
  workPhone: string | null;
  terminatedAt: string | null;
  createdByUserId: string;
  updatedByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  user: User;
  designation: Designation;
  department: Department; // TDB, mocking as designation for now as structure is same
  statusMeta?: EmployeeStatusMeta;
  /** Custom leave approvers (`leave_managers`), not the reporting manager. */
  managers?: EmployeeManagerAssignment[];
}

/**
 * The current reporting-line manager on an Employees list row
 * (`GET companies/:id/employees` returns it as `reportingManager`).
 */
export type EmployeeReportingManager = Omit<
  LeaveRowReportingManager,
  'profileImage'
>;

export interface EmployeeManagerAssignment {
  id: string;
  manager: Manager;
}

export type GetEmployeeListResponse = GetListResponse<Employee>;

export type SortBy = 'name' | 'dateOfJoining' | null;
export type SortOrder = 'ASC' | 'DESC' | null;

export interface GetEmployeeListParams extends OptionalPageLimit {
  companyId: string;
  status?: EmployeeStatus;
  search?: string;
}

export type EmployeeStatus = 'all' | 'active' | 'inactive';

export interface EmployeeStatusMeta {
  color: 'red' | 'green' | 'yellow' | 'orange' | 'gray';
  meaning:
    | 'inactiveUser'
    | 'activeAndLoggedInUser'
    | 'activeNeverLoggedInInvitationNotSentUser'
    | 'activeNeverLoggedInInvitationSentUser'
    | 'noEmailUser';
}

export interface GetEmployeeByIdResponse {
  message: string;
  data: Employee;
}

export interface EmployeePersonalInformation {
  id?: string;
  firstName: string;
  lastName: string;
  dateOfBirth: Date | undefined;
  phoneNumber: string | null;
  email: string;
  gender: Gender;
  profileImage: string | null;
}

export interface ResetPasswordResponse {
  message: string;
  data: {
    employeeId: string;
    email: string;
    rawPassword: string;
    portal: string;
    portalUrl: string;
  };
}

export interface EmployeeWorkInformation {
  id?: string;
  employeeReferenceId: string | null;
  departmentId: string | null;
  designationId: string | null;
  dateOfJoining: Date | undefined;
  status: boolean;
  personalEmail?: string | null;
  workEmail: string | null;
  workPhone: string | null;
  employmentType: string | null;
  contractEndDate?: string | null;
  partTimeWeeklyMinutes?: number | null;
  terminatedAt?: string | null;
}

/** Voluntary (resignation, retirement) or involuntary departure. */
export enum EmployeeTerminationType {
  Voluntary = 'voluntary',
  Involuntary = 'involuntary',
}

export enum EmployeeProbationStatus {
  Active = 'active',
  Completed = 'completed',
  Extended = 'extended',
}

export enum EmployeeCreationStatus {
  PENDING = 'pending',
  SUCCESS = 'success',
  FAILED = 'failed',
  IN_PROGRESS = 'inProgress',
}

/** Status of an employee CSV import job (mirrors backend `BulkJobStatus`). */
export enum BulkImportJobStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'inProgress',
  /** Every row was imported. */
  COMPLETED = 'completed',
  /** Some rows were imported and some failed. */
  PARTIAL = 'partial',
  /** No row was imported. */
  FAILED = 'failed',
}

/** Why an import row failed, or what to know about an imported row. */
export enum BulkRowIssueCode {
  NO_DEFAULT_BRANCH = 'no_default_branch',
  BRANCH_NOT_FOUND = 'branch_not_found',
  BRANCH_OUTSIDE_SCOPE = 'branch_outside_scope',
  BRANCH_REQUIRED = 'branch_required',
  BRANCH_NONE_ASSIGNED = 'branch_none_assigned',
  DEPARTMENT_NOT_FOUND = 'department_not_found',
  DEPARTMENT_CREATED = 'department_created',
  DESIGNATION_NOT_FOUND = 'designation_not_found',
  DESIGNATION_CREATED = 'designation_created',
  EMAIL_EXISTS = 'email_exists',
  EMAIL_INVALID = 'email_invalid',
  /** Blank Email without `No Email = yes` (D158). */
  EMAIL_REQUIRED = 'email_required',
  /** Info on a row created without an email. */
  NO_EMAIL = 'no_email',
  NO_EMAIL_WITH_ADDRESS = 'no_email_with_address',
  NO_EMAIL_INVALID = 'no_email_invalid',
  FIRST_NAME_REQUIRED = 'first_name_required',
  LAST_NAME_REQUIRED = 'last_name_required',
  DATE_OF_BIRTH_INVALID = 'date_of_birth_invalid',
  DATE_OF_JOINING_INVALID = 'date_of_joining_invalid',
  MANAGER_EMAIL_INVALID = 'manager_email_invalid',
  MANAGER_SELF = 'manager_self',
  MANAGER_NOT_FOUND = 'manager_not_found',
  MANAGER_IS_ADMIN_ONLY = 'manager_is_admin_only',
  MANAGER_NOT_SET = 'manager_not_set',
  GENDER_INVALID = 'gender_invalid',
  SALARY_INCOMPLETE = 'salary_incomplete',
  SALARY_AMOUNT_INVALID = 'salary_amount_invalid',
  PAY_TYPE_INVALID = 'pay_type_invalid',
  SALARY_DATE_INVALID = 'salary_date_invalid',
  PHONE_INVALID = 'phone_invalid',
  ADDRESS_INCOMPLETE = 'address_incomplete',
  COUNTRY_NOT_FOUND = 'country_not_found',
  PROVINCE_NOT_FOUND = 'province_not_found',
  PROVINCE_NEEDS_COUNTRY = 'province_needs_country',
  ADDRESS_EXISTS = 'address_exists',
  SYSTEM_ERROR = 'system_error',
  /** Billing paywall: the free tier is full and the company cannot grow. */
  BILLING_SEAT_LIMIT = 'billing_seat_limit',
}

export type BulkRowIssueSeverity = 'error' | 'warning' | 'info';

/**
 * One entry of an import row's `errorMessage`, keyed by CSV column. Rows
 * written before these codes existed hold a plain English string instead.
 */
export interface BulkRowIssue {
  code: BulkRowIssueCode;
  severity: BulkRowIssueSeverity;
  /** English fallback. */
  message: string;
  params?: Record<string, string | number>;
}

/** The values the import accepts for this uploader (template hint). */
export interface BulkImportTemplateValues {
  branches: string[];
  departments: string[];
  designations: string[];
  /** Unknown departments are created on import. */
  canCreateDepartments: boolean;
  /** Unknown job titles are created on import. */
  canCreateDesignations: boolean;
  /** The currency an imported starting salary is recorded in. */
  salaryCurrency: string;
}

export interface EmployeeEducationDocument {
  id: string;
  fileUrl: string;
}

export interface EmployeeEducation {
  id?: string;
  employeeId: string;
  educationLevel: string;
  institutionName: string;
  course: string;
  startDate: Date | null;
  endDate: Date | null;
  createdByUserId?: string;
  updatedByUserId?: string | null;
  createdAt?: string;
  updatedAt?: string | null;
  deletedAt?: string | null;
  /** Certificates attached to the qualification; the list endpoints join them. */
  documents?: EmployeeEducationDocument[];
}

export interface EmployeeFamily {
  id?: string;
  employeeId: string;
  name: string;
  firstName?: string | null;
  lastName?: string | null;
  phoneNumber?: string | null;
  isEmergencyContact?: boolean;
  relation: string;
  dateOfBirth: Date | undefined;
  gender: string;
  email: string;
  extraData: string;
  createdByUserId: string;
  updatedByUserId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
}

export interface GetEmployeeEducationListResponse {
  message: string;
  data: EmployeeEducation[];
}

export interface GetEmployeeFamilyListResponse {
  message: string;
  data: EmployeeFamily[];
}

export type EmployeeFamilyFormValues = Pick<
  EmployeeFamily,
  | 'name'
  | 'firstName'
  | 'lastName'
  | 'phoneNumber'
  | 'isEmergencyContact'
  | 'relation'
  | 'gender'
  | 'dateOfBirth'
  | 'email'
>;

export type EmployeeEducationFormValues = {
  educationLevel: string;
  institutionName: string;
  course?: string;
  startDate?: string | null;
  endDate?: string | null;
  documents?: string[];
};

export interface AddEmployee {
  user: {
    email: string;
    firstName: string;
    lastName: string;
    /** No email: no portal, no app, no notifications (D157). */
    hasNoEmail?: boolean;
    /** Attach the record to this administrator's account (D159). */
    linkUserId?: string;
  };
  designationId?: string;
  departmentId?: string;
  employeeReferenceId?: string;
  employmentType?: string;
  contractEndDate?: string;
  partTimeWeeklyMinutes?: number | null;
  dateOfJoining?: string;
  workPhone?: string;
  workEmail?: string;
}

export type AddEmployeeFormValues = Pick<
  AddEmployee,
  | 'user'
  | 'dateOfJoining'
  | 'designationId'
  | 'departmentId'
  | 'employeeReferenceId'
  | 'employmentType'
  | 'contractEndDate'
  | 'workEmail'
  | 'workPhone'
>;
export const EMPLOYMENT_TYPE_MAP: Record<string, string> = {
  full_time: 'Full Time',
  part_time: 'Part Time',
  contract: 'Contract',
  intern: 'Intern',
};

export const EDUCATION_LEVEL_MAP: Record<string, string> = {
  high_school: 'High School',
  diploma: 'Diploma',
  certificate: 'Certificate',
  bachelor: 'Bachelor',
  master: 'Masters',
  phd: 'PHD',
};

/** Every part of an address is optional; the API returns null for a missing part. */
export interface EmployeeAddress {
  id?: string;
  isPermanent?: boolean;
  addressLine_1: string | null;
  addressLine_2: string | null;
  city: string | null;
  provinceId: string | number | null;
  countryCode: string | null;
  postalCode: string | null;
}

export interface Countries {
  code: string;
  name: string;
}
export interface GetCountriesResponse {
  message: string;
  data: Countries[];
}

export interface Provinces {
  id: number;
  name: string;
  countryCode: string;
  code: string;
}

export interface GetProvincesResponse {
  message: string;
  data: Provinces[];
}

export interface GetEmployeeAddress {
  message: string;
  data: EmployeeAddress[];
}

export interface EmployeeAddressWithDetails extends EmployeeAddress {
  country: Countries;
  province: Provinces;
}

export interface GetEmployeeAddressWithDetails {
  message: string;
  data: EmployeeAddressWithDetails[];
}

export interface EmployeeLeave {
  id: string;
  employeeId: string;
  requestType: string; // e.g., "Sick Leave", "Casual Leave"
  from: string; // start date
  to: string; // end date
  status: 'Approved' | 'Rejected' | 'Pending';
  comments?: string;
}

export interface CompanyDocumentType {
  id: string;
  companyId: string;
  name: string;
  status: boolean;
  description: string;
  /** Whether documents of this type carry an expiry date. */
  hasExpiry?: boolean;
}

export interface GetCompanyDocumentType {
  message: string;
  data: CompanyDocumentType[];
}

export type AddCompanyDocumentTypeFormValues = Pick<
  CompanyDocumentType,
  'name' | 'description'
> & { hasExpiry?: boolean };

export interface EmployeeDocument {
  id: string;
  employeeId: string;
  companyId: string;
  documentTypeId: string;
  status: string;
  documentUrl: string;
  documentType?: CompanyDocumentType;
}

export interface GetEmployeeDocuments {
  messagse: string;
  data: EmployeeDocument[];
}

/** Why a pay change happened (the Adjust compensation dialog). */
export type SalaryReason =
  | 'merit'
  | 'promotion'
  | 'market_adjustment'
  | 'cost_of_living'
  | 'role_change'
  | 'other';

/** Every salary reason, in the order the dialog lists them. */
export const SALARY_REASONS: SalaryReason[] = [
  'merit',
  'promotion',
  'market_adjustment',
  'cost_of_living',
  'role_change',
  'other',
];

/**
 * Where a compensation record came from: `manual` today, `import` once a
 * payroll sync upserts records by their `externalRef`.
 */
export type CompensationSource = 'manual' | 'import';

export enum PayType {
  HOURLY = 'hourly',
  WEEKLY = 'weekly',
  BI_WEEKLY = 'bi_weekly',
  SEMI_MONTHLY = 'semi_monthly',
  MONTHLY = 'monthly',
  ANNUAL = 'annual',
}

export interface EmployeeSalary {
  id: string;
  employeeId: string;
  amount: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
  payType: PayType;
  currency: string;
  notes?: string | null;
  /** Why the pay changed (set by Adjust compensation). */
  reason?: SalaryReason | null;
  /** Payroll readiness: `import` rows come from a payroll sync. */
  source?: CompensationSource;
  externalRef?: string | null;
  /** Hours worked per week this pay is for; null follows the schedule. */
  weeklyHours?: number | null;
  createdAt: string;
  updatedAt: string;
  createdByUserId: string;
  updatedByUserId?: string | null;
}

export type EmployeeSalaryFormValues = Pick<
  EmployeeSalary,
  'amount' | 'effectiveFrom' | 'effectiveTo' | 'payType' | 'currency' | 'notes'
> & {
  reason?: SalaryReason | '' | null;
  /** Empty follows the schedule. */
  weeklyHours: number | '';
};

export type GetEmployeeSalaryListResponse = GetListResponse<EmployeeSalary>;

export interface EmployeeImportJobsParams {
  companyId: string;
  page?: number;
  limit?: number;
}

export interface ImportJobResponse {
  id: string;
  fileName: string;
  title: string;
  description?: string;
  status: string;
  createdAt: string;
  createdByUser?: Pick<User, 'id' | 'firstName' | 'lastName' | 'email'>;
}

export interface UploadCsvFormValues {
  file: File | null;
}
export interface UploadCsvFormProps {
  companyId: string;
}

export interface EmployeeImportJobDataParams extends OptionalPageLimit {
  companyId: string;
  jobId: string;
}

export interface ImportJobRowData {
  email: string;
  firstname: string;
  lastname: string;
  department: string;
  designation: string;
}

export interface ImportJobRow {
  id: string;
  status: EmployeeCreationStatus;
  data: ImportJobRowData;
  errorMessage: Partial<ImportJobRowData>;
  updatedAt: string;
}

export interface ImportJobStats {
  totalCount: number;
  successCount: number;
  failedCount: number;
  pendingCount: number;
}

export interface ManagerUser {
  id: string;
  firstName: string;
  lastName: string;
}

export interface Manager {
  id: string;
  user: ManagerUser;
}

export interface AssignManagersPayload {
  employeeId: string;
  managerIds: string[];
}

export interface CreateEmployeeBankDetailsPayload {
  countryCode: string;
  bankName: string;
  accountHolderName: string;
  accountNumber: string;
  fields: {
    countryBankingFieldId: string;
    fieldValue: string;
  }[];
}

export interface EmployeeBankDetailsFormValues {
  countryCode: string;
  bankName: string;
  accountHolderName: string;
  accountNumber: string;

  // dynamic fields
  countryFields: Record<string, string>;
}

export interface CountryBankingField {
  id: string;
  countryCode: string;
  fieldKey: string;
  fieldLabel: string;
  isRequired: boolean;
}

export interface EmployeeBankFieldValue {
  id: string;
  countryBankingFieldId: string;
  fieldValue: string;
  countryBankingField: CountryBankingField;
}

export interface EmployeeBankDetails {
  id: string;
  countryCode: string;
  bankName: string;
  accountNumber: string;
  accountHolderName: string;
  fieldValues: EmployeeBankFieldValue[];

  // optional helper for the form: map of fieldKey → fieldValue
  countryFields?: Record<string, string>;
}

export interface EmployeeBankDetailsApi {
  id: string;
  countryCode: string;
  bankName: string;
  accountNumber: string;
  accountHolderName: string;
  fieldValues: EmployeeBankFieldValue[];
}

export interface GetEmployeeBankDetailsResponse {
  message: string;
  data: EmployeeBankDetailsApi[];
}

export interface BaseBankAccountParams {
  companyId: string;
  employeeId: string;
}

export interface GetBankAccountFieldsPayload extends BaseBankAccountParams {
  code: string;
}

export interface CreateEmployeeBankDetailsAPIPayload
  extends BaseBankAccountParams {
  payload: CreateEmployeeBankDetailsPayload;
}

export interface UpdateEmployeeBankDetailsAPIPayload
  extends BaseBankAccountParams {
  id: string;
  payload: CreateEmployeeBankDetailsPayload;
}

export interface DeleteEmployeeBankDetailsAPIPayload
  extends BaseBankAccountParams {
  bankAccountId: string;
}
